// Tiny platform probes so subsystems can adapt to browser vs Node vs game host.
// All are safe to call in any environment (no throws).

export function hasLocalStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const k = '__bf_probe__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

export function hasDOM() {
  return typeof document !== 'undefined' && typeof document.createElement === 'function';
}

// True when running inside a Capacitor native app (Android/iOS), false on web.
// Safe everywhere — no throw if Capacitor isn't present.
export function isCapacitorNative() {
  try {
    const cap = typeof globalThis !== 'undefined' ? globalThis.Capacitor : undefined;
    if (!cap) return false;
    if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
    return !!cap.isNative;
  } catch {
    return false;
  }
}

// 'android' | 'ios' | 'web' (best effort).
export function capacitorPlatform() {
  try {
    const cap = typeof globalThis !== 'undefined' ? globalThis.Capacitor : undefined;
    if (cap && typeof cap.getPlatform === 'function') return cap.getPlatform();
  } catch { /* ignore */ }
  return 'web';
}

// True when the game is running inside an iframe (e.g. embedded in the Butterfly
// website). Cross-origin access to window.top can throw — that itself means we're
// embedded, so we treat a throw as true.
export function isEmbedded() {
  try {
    if (typeof window === 'undefined') return false;
    return window.parent && window.parent !== window;
  } catch {
    return true;
  }
}

// True for local development hosts: localhost, loopback, *.local, or a private
// LAN IP (e.g. `vite --host` on a phone). Safe everywhere — no throw.
export function isDevEnvironment() {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    const h = (window.location.hostname || '').toLowerCase();
    if (!h || h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h === '::1') return true;
    if (h.endsWith('.local') || h.endsWith('.localhost')) return true;
    // Private IPv4 ranges (LAN device testing).
    if (/^10\./.test(h) || /^192\.168\./.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h)) return true;
    return false;
  } catch {
    return false;
  }
}

// Best-effort name of the runtime/host the game is embedded in. Used for logging
// and could later branch ad/identity behavior per portal.
export function platformName() {
  if (isCapacitorNative()) return `capacitor-${capacitorPlatform()}`;
  if (typeof window !== 'undefined') {
    if (window.CrazyGames) return 'crazygames';
    if (window.GamePix) return 'gamepix';
    if (window.ysdk) return 'yandex';
    try { if (window.self !== window.top) return 'iframe'; } catch { return 'iframe'; }
    return 'web';
  }
  if (typeof process !== 'undefined' && process.versions?.node) return 'node';
  return 'unknown';
}
